/**
 * Document owner roles for missing document tracking.
 * Maps to backend owner values sent via the API.
 *
 * To add a new role:
 * 1. Add entry to DOC_OWNER
 * 2. Add entry to DOC_OWNER_LABELS
 * 3. Add entry to DOC_OWNER_BADGE_CONFIG
 * 4. Add the role to relevant partner configs' documentOwnerRoles array
 */
export const DOC_OWNER = {
  AGENT: 'AGENT',
  PROCESSOR: 'PROCESSOR',
  CLOSING_MEMBER: 'CLOSING_MEMBER',
} as const;

/** Union type of all valid document owner values */
export type DocOwnerValue = (typeof DOC_OWNER)[keyof typeof DOC_OWNER];

/** Human-readable labels for each owner role */
export const DOC_OWNER_LABELS: Record<DocOwnerValue, string> = {
  [DOC_OWNER.AGENT]: 'Agent',
  [DOC_OWNER.PROCESSOR]: 'Processor',
  [DOC_OWNER.CLOSING_MEMBER]: 'Closing Member',
};

/** Badge color configuration for each owner role (Tailwind classes + hex for inline styles) */
export const DOC_OWNER_BADGE_CONFIG: Record<
  DocOwnerValue,
  { bgClass: string; textClass: string; hexColor: string }
> = {
  [DOC_OWNER.AGENT]: {
    bgClass: 'bg-blue-100',
    textClass: 'text-blue-700',
    hexColor: '#2563eb',
  },
  [DOC_OWNER.PROCESSOR]: {
    bgClass: 'bg-purple-100',
    textClass: 'text-purple-700',
    hexColor: '#7e22ce',
  },
  [DOC_OWNER.CLOSING_MEMBER]: {
    bgClass: 'bg-teal-100',
    textClass: 'text-teal-700',
    hexColor: '#0d9488',
  },
};

/** Default set of available owner roles when partner doesn't specify documentOwnerRoles */
export const DEFAULT_DOC_OWNER_ROLES: DocOwnerValue[] = [
  DOC_OWNER.AGENT,
  DOC_OWNER.PROCESSOR,
];

/** Get the display label for a document owner value. Falls back to the raw value if unknown. */
export const getDocOwnerLabel = (owner: string): string =>
  DOC_OWNER_LABELS[owner as DocOwnerValue] ?? owner;

/** Get the badge config for a document owner value. Falls back to neutral gray if unknown. */
export const getDocOwnerBadgeConfig = (owner: string) =>
  DOC_OWNER_BADGE_CONFIG[owner as DocOwnerValue] ?? {
    bgClass: 'bg-gray-100',
    textClass: 'text-gray-700',
    hexColor: '#374151',
  };
